import { useCallback, useEffect, useRef, useState } from "react";
import registerServiceWorker from "../registerServiceWorker";
import { authTokenStore } from "../utils/authTokenStore";
import { getErrorMessage } from "./authErrors";
import { createAuthAdapter } from "./createAuthAdapter";
import { IAuthAdapter, initialAuthState, TAuthState } from "./types";

export const useAuthController = () => {
  const [state, setState] = useState<TAuthState>(initialAuthState);
  const adapterRef = useRef<IAuthAdapter | null>(null);
  const wasLoggedInRef = useRef(false);

  useEffect(() => {
    let isActive = true;
    const adapter = createAuthAdapter();
    const unregisterRefreshHandler = authTokenStore.registerRefreshHandler(() =>
      adapter.refreshToken(),
    );
    adapterRef.current = adapter;

    void adapter
      .initialize({
        onAuthenticated: (session) => {
          if (!isActive) return;

          authTokenStore.set(session.idToken);
          setState({
            msalInstance: session.msalInstance,
            isLoggedIn: true,
            isAuthenticating: false,
            idToken: session.idToken,
            authError: null,
            isMobileAuth: session.isMobileAuth,
          });
        },
        onError: (error) => {
          if (!isActive) return;

          setState((currentState) => ({
            ...currentState,
            isAuthenticating: false,
            authError: getErrorMessage(error),
          }));
        },
        onLoggedOut: () => {
          if (!isActive) return;

          authTokenStore.clear();
          setState((currentState) => ({
            ...currentState,
            isLoggedIn: false,
            isAuthenticating: false,
            idToken: "",
            authError: null,
          }));
        },
      })
      .catch((error: unknown) => {
        if (!isActive) return;

        setState((currentState) => ({
          ...currentState,
          isAuthenticating: false,
          authError: getErrorMessage(error),
        }));
      });

    return () => {
      isActive = false;
      unregisterRefreshHandler();
      adapter.dispose();

      if (adapterRef.current === adapter) adapterRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (!wasLoggedInRef.current && state.isLoggedIn && !state.isMobileAuth) {
      registerServiceWorker();
    }

    wasLoggedInRef.current = state.isLoggedIn;
  }, [state.isLoggedIn, state.isMobileAuth]);

  const refreshToken = useCallback(async () => {
    const adapter = adapterRef.current;
    if (!adapter) throw new Error("Autenticacao nao inicializada");

    try {
      return await adapter.refreshToken();
    } catch (error: unknown) {
      setState((currentState) => ({
        ...currentState,
        authError: getErrorMessage(error),
      }));
      throw error;
    }
  }, []);

  const performLoginRedirect = useCallback(async () => {
    try {
      await adapterRef.current?.login();
    } catch (error: unknown) {
      setState((currentState) => ({
        ...currentState,
        isAuthenticating: false,
        authError: getErrorMessage(error),
      }));
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await adapterRef.current?.logout();
    } catch (error: unknown) {
      setState((currentState) => ({
        ...currentState,
        authError: getErrorMessage(error),
      }));
    }
  }, []);

  return {
    state,
    refreshToken,
    performLoginRedirect,
    logout,
  };
};
